// ─── Timeline → RenderInputProps serializer ─────────────────────────────────
// Turns the editor's in-memory state (clips, aspectRatio, duration) into the
// JSON payload the Remotion backend consumes. This is the ONLY place that
// knows how to:
//   • Resolve a playable URL for every media clip (re-signing from storage
//     path/asset id so the URL stays valid throughout a long render).
//   • Drop clips that can't render (empty text, AI still generating, etc.)
//     and report them back so the UI can warn the user.
//   • Strip editor-only fields (name, color, thumbnail, aiStatus, UI flags)
//     the renderer doesn't need.
//
// The resulting object matches `RenderInputProps` in
// `remotion/src/types.ts`. Keep both in sync.

import type { EncuadreDeClip } from '../encuadreDeClip';
import { esEncuadreNeutro } from '../encuadreDeClip';
import { versionActual, type AnimacionPorCodigo } from '../../animations/codigo/versiones';
import { duracionNaturalDeAnimacion } from '../../animations/codigo/ventana';
import { getAssetSignedUrl } from '../../utils/projectAssets';
import { getBrandAssetSignedUrl } from '../../utils/brandAssets';
import { supabase } from '../supabase';
import type {
  RenderAspectRatio,
  RenderClip,
  RenderClipType,
  RenderTransitionType,
  SerializeResult,
  SkippedClip,
} from './types';

// Shape of the editor's in-memory clip. We don't import it to avoid a tight
// coupling to VideoEditor.tsx (which has a LOT of UI-only fields); instead
// we accept `any` for unknown optional keys and only read what we need.
// The fields below MUST match `TimelineClip` in
// `project/src/pages/VideoEditor.tsx`.
export interface EditorClipInput {
  id: string;
  type: RenderClipType;
  name?: string;
  startTime: number;
  duration: number;
  src?: string;
  mediaOffset?: number;
  /** Duración del material (video/audio) o de la animación por código entera. */
  sourceDuration?: number;
  volume?: number;
  trackIndex?: number;
  /** Capa de apilado (la calcula el editor con el orden de las filas). */
  z?: number;
  transitionIn?: {
    type: RenderTransitionType;
    durationSec: number;
  };
  storagePath?: string;
  assetId?: string;
  aiStatus?: 'draft' | 'generating' | 'ready' | 'error';
  // Text clip fields
  text?: string;
  fontFamily?: string;
  fontSizePct?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  textXPct?: number;
  textYPct?: number;
  /** Giro del elemento en grados. */
  elementRotation?: number;
  /** Velocidad del clip de video (0.5..2). */
  speed?: number;
  /** Encuadre manual del clip (escala, posición, giro, recorte). */
  encuadre?: EncuadreDeClip;
  /** Opacidad de la capa, 0..1. */
  opacity?: number;
  /** URL estable de una capa de imagen ajena a los assets del proyecto (logo). */
  imageUrl?: string;
  /** Ancho/alto natural de una capa de imagen. */
  imageAspect?: number;
  /** Ancho explícito de la caja de texto, en % de la canvas. */
  textWidthPct?: number;
  // Motion de entrada / salida (texto y animaciones).
  enterAnimation?: string;
  enterDurationSec?: number;
  exitAnimation?: string;
  exitDurationSec?: number;
  // Plantilla animada.
  animationTemplate?: string;
  animationProps?: Record<string, unknown>;
  animXPct?: number;
  animYPct?: number;
  animScale?: number;
  /** Animación escrita por el agente (en vez de plantilla). */
  animationCodigo?: AnimacionPorCodigo;
}

/** Una tipografía subida por el usuario, tal como la tiene el editor. */
export interface EditorFuentePersonalizada {
  /** El `font-family` con el que el editor la registró (y con el que los clips la piden). */
  family: string;
  /** Ruta en el bucket de brand assets; se firma acá, al exportar. */
  storagePath: string;
}

export interface SerializeInput {
  clips: EditorClipInput[];
  aspectRatio: RenderAspectRatio;
  /** Total timeline length in seconds. */
  durationInSeconds: number;
  /** Defaults to 30. */
  fps?: number;
  /**
   * Las tipografías subidas que el editor tiene cargadas. Se mandan al render
   * SÓLO las que algún clip de texto usa: el renderer se baja cada archivo
   * antes del frame 0, y mandarle las diez de la marca para un video que usa
   * una es pagar nueve descargas por nada.
   */
  fuentesPersonalizadas?: EditorFuentePersonalizada[];
}

// Resolve a playable HTTPS URL for a single media clip. Returns null if
// no source can be found / signed. The caller decides whether to skip
// the clip or hard-fail the export.
async function resolveClipUrl(
  clip: EditorClipInput,
): Promise<{ url: string | null; reason?: string }> {
  // 1. Prefer `storagePath` — most reliable, re-signable on every export.
  if (clip.storagePath) {
    const signed = await getAssetSignedUrl(clip.storagePath);
    if (signed) return { url: signed };
    return { url: null, reason: 'signed-url-failed (storagePath)' };
  }
  // 2. Fall back to looking up by assetId → storage_path → sign.
  if (clip.assetId) {
    const { data, error } = await supabase
      .from('video_project_assets')
      .select('storage_path')
      .eq('id', clip.assetId)
      .maybeSingle();
    if (!error && data?.storage_path) {
      const signed = await getAssetSignedUrl(data.storage_path as string);
      if (signed) return { url: signed };
      return { url: null, reason: 'signed-url-failed (assetId)' };
    }
  }
  // 3. Last resort: use whatever `src` the clip already carries. This is
  //    the path for AI-generated clips whose output lives on an external
  //    bucket (R2/S3) and isn't mirrored in video_project_assets. If the
  //    URL is short-lived, the render backend will fetch it before expiry
  //    when firing the render.
  if (clip.src && /^https?:\/\//i.test(clip.src)) return { url: clip.src };
  // 4. Capa de imagen con URL estable propia (el logo de la marca, que no vive
  //    en los assets del proyecto). Al abrir un proyecto guardado es lo ÚNICO
  //    que tiene: `src` se descarta al guardar y no hay storagePath ni assetId
  //    de dónde reconstruirlo.
  if (clip.imageUrl && /^https?:\/\//i.test(clip.imageUrl))
    return { url: clip.imageUrl };

  return { url: null, reason: 'no storagePath/assetId/src' };
}

// Map an editor clip to a renderer clip. Returns either a `RenderClip`
// ready to send, or a `SkippedClip` reason if it can't render.
async function mapClip(
  clip: EditorClipInput,
): Promise<RenderClip | SkippedClip> {
  // ── Text clips ────────────────────────────────────────────────────────
  if (clip.type === 'text') {
    const text = (clip.text ?? '').trim();
    if (!text) {
      return { id: clip.id, name: clip.name, reason: 'text-empty' };
    }
    return {
      id: clip.id,
      type: 'text',
      startTime: clip.startTime,
      duration: clip.duration,
      text: clip.text,
      fontFamily: clip.fontFamily,
      fontSizePct: clip.fontSizePct,
      fontWeight: clip.fontWeight,
      fontStyle: clip.fontStyle,
      textColor: clip.textColor,
      textAlign: clip.textAlign,
      textXPct: clip.textXPct,
      textYPct: clip.textYPct,
      elementRotation: clip.elementRotation,
      // La CAPA. Sin esto el render apilaba los textos con su regla vieja
      // (texto siempre arriba del video), así que un texto puesto en una fila
      // POR DEBAJO de un video salía a la vista igual — justo al revés que en
      // el preview.
      z: clip.z,
      // El ancho de la caja: si el usuario la estiró, los cortes de línea
      // dependen de él. Sin mandarlo, el MP4 partía el texto en otros
      // renglones.
      textWidthPct: clip.textWidthPct,
      // Entrada y salida (typewriter, cascada, slides…).
      enterAnimation: clip.enterAnimation,
      enterDurationSec: clip.enterDurationSec,
      exitAnimation: clip.exitAnimation,
      exitDurationSec: clip.exitDurationSec,
    };
  }

  // ── Plantillas animadas ("Elementos") ─────────────────────────────────
  // No tienen archivo: son componentes de Remotion que el render resuelve por
  // id contra el MISMO registro que usa el preview. Antes caían en el camino
  // de los medios, se quedaban sin URL y el export los descartaba como
  // "missing-source": desaparecían del MP4 sin que nadie lo dijera.
  if (clip.type === 'animation') {
    // ── Animación por código (ADR-2026-09-24-animacion-por-codigo) ─────
    // Viaja SÓLO la versión actual, con las URLs de sus fotos re-firmadas
    // como las de cualquier imagen: el render puede tardar más que la firma
    // con la que se creó el clip.
    if (clip.animationCodigo) {
      const anim = clip.animationCodigo;
      const version = versionActual(anim);
      const assets = await Promise.all(
        anim.assets.map(async a => ({
          url: a.storagePath ? (await getAssetSignedUrl(a.storagePath)) ?? a.url : a.url,
          descripcion: a.descripcion,
        })),
      );
      return {
        id: clip.id,
        type: 'animation',
        startTime: clip.startTime,
        duration: clip.duration,
        z: clip.z,
        animXPct: clip.animXPct,
        animYPct: clip.animYPct,
        animScale: clip.animScale,
        enterAnimation: clip.enterAnimation,
        enterDurationSec: clip.enterDurationSec,
        exitAnimation: clip.exitAnimation,
        exitDurationSec: clip.exitDurationSec,
        // El código se dibuja con su duración real y el clip muestra una ventana
        // (ADR-2026-09-26-las-animaciones-se-recortan-como-un-video).
        animationCodigo: {
          codigo: version.codigo,
          tipo: anim.tipo,
          assets,
          marca: anim.marca,
          duracionSeg: duracionNaturalDeAnimacion(clip),
          desdeSeg: clip.mediaOffset ?? 0,
        },
      };
    }
    if (!clip.animationTemplate) {
      return { id: clip.id, name: clip.name, reason: 'animation-sin-plantilla' };
    }
    return {
      id: clip.id,
      type: 'animation',
      startTime: clip.startTime,
      duration: clip.duration,
      z: clip.z,
      animationTemplate: clip.animationTemplate,
      animationProps: clip.animationProps,
      animXPct: clip.animXPct,
      animYPct: clip.animYPct,
      animScale: clip.animScale,
      enterAnimation: clip.enterAnimation,
      enterDurationSec: clip.enterDurationSec,
      exitAnimation: clip.exitAnimation,
      exitDurationSec: clip.exitDurationSec,
    };
  }

  // ── AI-generated clips that aren't ready yet are silently skipped.
  //    The UI-level preflight (see `preflightExport`) surfaces these so the
  //    user can wait for generation to finish before retrying.
  if (clip.type === 'ai-generated' && clip.aiStatus && clip.aiStatus !== 'ready') {
    return { id: clip.id, name: clip.name, reason: 'ai-not-ready', detail: clip.aiStatus };
  }

  // ── Media clips (video/image/audio/ai-generated-ready): resolve URL.
  const { url, reason } = await resolveClipUrl(clip);
  if (!url) {
    return {
      id: clip.id,
      name: clip.name,
      reason: reason?.startsWith('signed-url') ? 'signed-url-failed' : 'missing-source',
      detail: reason,
    };
  }

  return {
    id: clip.id,
    type: clip.type,
    startTime: clip.startTime,
    duration: clip.duration,
    src: url,
    mediaOffset: clip.mediaOffset,
    volume: clip.volume,
    // Igual que el encuadre: sólo viaja si no es 1x.
    speed:
      typeof clip.speed === "number" && Math.abs(clip.speed - 1) > 0.001
        ? clip.speed
        : undefined,
    trackIndex: clip.trackIndex,
    z: clip.z,
    transitionIn: clip.transitionIn,
    // Sólo viaja si el usuario lo tocó: un encuadre neutro es exactamente lo
    // que el renderer hace por defecto, y mandarlo engordaría el payload de
    // cada clip de todos los proyectos para no cambiar un pixel.
    encuadre: esEncuadreNeutro(clip.encuadre) ? undefined : clip.encuadre,
    // Igual que el encuadre: sólo viaja si no es el valor por defecto.
    opacity:
      typeof clip.opacity === "number" && clip.opacity < 1
        ? clip.opacity
        : undefined,
    // Sólo para imágenes: el render lo usa para recortar sobre la imagen.
    imageAspect: clip.type === "image" ? clip.imageAspect : undefined,
    // Sólo para imágenes: entrada y salida, como un texto o un Elemento
    // (ADR-2026-09-27-las-capas-de-imagen-entran-y-salen). Video y ai-generated
    // no las tienen: su "entrada" es `transitionIn`.
    ...(clip.type === "image"
      ? {
          enterAnimation: clip.enterAnimation,
          enterDurationSec: clip.enterDurationSec,
          exitAnimation: clip.exitAnimation,
          exitDurationSec: clip.exitDurationSec,
        }
      : {}),
  };
}

// Public API: serialize the editor timeline into the exact payload the
// Remotion backend will consume. Does NOT hit the render backend itself —
// the caller passes the returned `input` to whatever endpoint kicks off
// the render job.
export async function serializeTimeline(
  params: SerializeInput,
): Promise<SerializeResult> {
  const {
    clips,
    aspectRatio,
    durationInSeconds,
    fps = 30,
    fuentesPersonalizadas = [],
  } = params;

  // Resolve all clip URLs in parallel — signing N URLs sequentially would
  // push export latency into multi-second territory on large projects.
  const mapped = await Promise.all(clips.map(c => mapClip(c)));

  const out: RenderClip[] = [];
  const skipped: SkippedClip[] = [];
  for (const m of mapped) {
    if ('reason' in m) skipped.push(m);
    else out.push(m);
  }

  // Tipografías subidas: sólo las que quedaron EN USO por un clip de texto que
  // de verdad se va a renderizar (`out`, no `clips`: un texto vacío se descarta
  // arriba y no tiene sentido bajar su fuente). El match es por nombre sin
  // distinguir mayúsculas, igual que `fontStackFor`.
  const familiasUsadas = new Set(
    out
      .filter(c => c.type === 'text' && c.fontFamily)
      .map(c => (c.fontFamily as string).trim().toLowerCase()),
  );
  const resueltas = await Promise.all(
    fuentesPersonalizadas
      .filter(f => familiasUsadas.has(f.family.trim().toLowerCase()))
      .map(async f => ({
        family: f.family,
        url: await getBrandAssetSignedUrl(f.storagePath),
      })),
  );
  const fuentesFirmadas = resueltas.filter(
    (f): f is { family: string; url: string } => f.url !== null,
  );
  // La que no se pudo firmar NO frena el export —el clip se renderiza igual—
  // pero sale con la tipografía de fallback: otras métricas, otros cortes de
  // línea. Antes se descartaba en silencio y el cambio se descubría mirando el
  // MP4, igual que pasaba con los clips que quedaban afuera; ahora viaja al
  // llamador para avisarlo con ellos.
  const fuentesSinResolver = resueltas
    .filter(f => f.url === null)
    .map(f => f.family);

  return {
    input: {
      clips: out,
      aspectRatio,
      durationInSeconds,
      fps,
      ...(fuentesFirmadas.length > 0
        ? { fuentesPersonalizadas: fuentesFirmadas }
        : {}),
    },
    skipped,
    fuentesSinResolver,
  };
}

// Lightweight preflight that checks the timeline WITHOUT hitting network.
// Useful for the export modal to warn the user about obvious issues before
// they commit to a render (e.g. "5 AI clips still generating").
export function preflightExport(params: {
  clips: EditorClipInput[];
  durationInSeconds: number;
}): {
  blockers: string[];
  warnings: string[];
} {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const { clips, durationInSeconds } = params;

  if (clips.length === 0) {
    blockers.push('La timeline está vacía — agregá al menos un clip antes de exportar.');
  }
  if (!isFinite(durationInSeconds) || durationInSeconds <= 0) {
    blockers.push('La duración del proyecto no es válida.');
  }

  const aiPending = clips.filter(
    c => c.type === 'ai-generated' && c.aiStatus && c.aiStatus !== 'ready',
  );
  if (aiPending.length > 0) {
    blockers.push(
      `Hay ${aiPending.length} clip(s) de IA que todavía no terminaron de generarse. ` +
        `Esperá a que terminen o eliminalos antes de exportar.`,
    );
  }

  const textEmpty = clips.filter(c => c.type === 'text' && !(c.text ?? '').trim());
  if (textEmpty.length > 0) {
    warnings.push(
      `${textEmpty.length} clip(s) de texto están vacíos y se van a omitir del MP4 final.`,
    );
  }

  const mediaNoSource = clips.filter(
    c =>
      c.type !== 'text' &&
      // Las plantillas animadas no tienen archivo: se resuelven por id contra
      // el registro de animaciones. Contarlas acá hacía que el preflight
      // avisara que "no tienen URL resoluble" por cada Elemento del proyecto.
      c.type !== 'animation' &&
      !c.storagePath &&
      !c.assetId &&
      !(c.src && /^https?:\/\//i.test(c.src)) &&
      !(c.imageUrl && /^https?:\/\//i.test(c.imageUrl)),
  );
  if (mediaNoSource.length > 0) {
    warnings.push(
      `${mediaNoSource.length} clip(s) de medio no tienen URL resoluble y se van a omitir.`,
    );
  }

  const animSinPlantilla = clips.filter(
    c => c.type === 'animation' && !c.animationTemplate && !c.animationCodigo,
  );
  if (animSinPlantilla.length > 0) {
    warnings.push(
      `${animSinPlantilla.length} elemento(s) animado(s) no tienen plantilla y se van a omitir.`,
    );
  }

  return { blockers, warnings };
}
